import {Button} from '@wandb/weave/components';
import {debounce} from 'lodash';
import React from 'react';
import {twMerge} from 'tailwind-merge';

import {SettingType} from '../../../../services/analytics/workspaceSettingsEvents';
import {
  calculateIsSettingEnabled,
  SettingOverrideCounts,
} from '../../../../state/views/workspaceSettings/settingOverrideUtils';
import {ClearSettingsRows} from '../components/ClearSettingsRow';
import {NumberRangeInput} from '../components/NumberRangeInput';
import {ResetSettingButton} from '../components/ResetSettingButton';
import {SettingIndentedRow} from '../components/SettingIndentedRow';
import {SettingLabel} from '../components/SettingLabel';
import {SettingLogToggle} from '../components/SettingLogToggle';
import {useClearSettingRows} from '../hooks/useClearSettingRows';
import {ClearSettingFnType} from '../hooks/useSettingOverrides';
import {LinePlotSettings, SettingLevel} from '../types';

const WORKSPACE_Y_AXIS_TOOLTIP =
  'Defining a manual Y-axis range will constrain all line plots in this workspace. For highly variable metrics, consider setting the range at the section level. Clear the Min and Max fields to reset the range.';

const EMPTY_Y_AXIS_SETTINGS = {
  yAxisMin: undefined,
  yAxisMax: undefined,
  yLogScale: undefined,
};

export type LinePlotYAxisProps = {
  clearSetting: ClearSettingFnType;
  /**
   * These are the values that are displayed to the user. We want to show the
   * inherited values when relevant.
   */
  displayValues: {
    yAxisMax?: number;
    yAxisMin?: number;
    yLogScale: boolean; // we need a value to feed into the toggle comp
  };
  numOverrides: SettingOverrideCounts;
  settingLevel: SettingLevel;
  /**
   * These are the values that match in the view spec depending on the setting level.
   */
  values: {
    yAxisMax?: number;
    yAxisMin?: number;
    yLogScale?: boolean; // this can be undefined to indicate user hasn't touched the setting
  };
  trackSetting: (setting: SettingType, value: string | undefined) => void;
  updateSetting: (settings: Partial<LinePlotSettings>) => void;
};

export const LinePlotYAxis = ({
  clearSetting,
  displayValues,
  numOverrides,
  settingLevel,
  trackSetting,
  updateSetting,
  values,
}: LinePlotYAxisProps) => {
  const clearSettingRows = useClearSettingRows({
    clearSetting,
    emptySettings: EMPTY_Y_AXIS_SETTINGS,
    numOverrides,
    settingKey: 'yAxis',
    settingLevel,
  });

  return (
    <div
      className={twMerge('flex flex-col', 'gap-8')}
      data-test="y-axis-setting">
      <div className="flex justify-between">
        <YAxisLabel type={settingLevel} />
        <div className="item-center flex flex-row gap-8">
          <ResetSettingButton
            settingLevel={settingLevel}
            isEnabled={calculateIsSettingEnabled('yAxis', values)}
            onButtonClick={() =>
              clearSetting(settingLevel, 'yAxis', EMPTY_Y_AXIS_SETTINGS)
            }
          />
        </div>
      </div>
      <SettingIndentedRow>
        <YAxisRange
          yLogScale={displayValues.yLogScale}
          yAxisMin={displayValues.yAxisMin}
          yAxisMax={displayValues.yAxisMax}
          updateSetting={updateSetting}
          trackSetting={trackSetting}
        />
      </SettingIndentedRow>
      <ClearSettingsRows rows={clearSettingRows} />
    </div>
  );
};

export const YAxisRange = ({
  yLogScale,
  yAxisMax,
  yAxisMin,
  updateSetting,
  trackSetting,
}: {
  yLogScale: boolean;
  yAxisMax?: number;
  yAxisMin?: number;
  updateSetting: (settings: Partial<LinePlotSettings>) => void;
  trackSetting: (setting: SettingType, value: string | undefined) => void;
}) => {
  const debouncedUpdateSetting = debounce(updateSetting, 500);
  return (
    <div className="flex">
      <SettingLabel text="Range" className="ml-14 w-[87px]" />
      <div className="flex gap-8">
        <NumberRangeInput
          minField="yAxisMin"
          maxField="yAxisMax"
          minValue={yAxisMin}
          maxValue={yAxisMax}
          updateSetting={debouncedUpdateSetting}
          trackSetting={trackSetting}
        />
      </div>
      <SettingLogToggle
        id="yLogScaleToggle"
        className="ml-16"
        isChecked={yLogScale}
        onCheckedChange={newValue => {
          debouncedUpdateSetting({yLogScale: newValue});
          trackSetting('yLogScale', newValue.toString());
        }}
      />
    </div>
  );
};

const YAxisLabel = ({type}: {type: SettingLevel}) => {
  if (type === 'workspace') {
    return (
      <div className="flex items-center gap-4">
        <SettingLabel className="w-fit" text="Y axis" />
        <Button
          size="small"
          variant="ghost"
          icon="info"
          tooltipProps={{className: 'w-[20rem]'}}
          tooltip={WORKSPACE_Y_AXIS_TOOLTIP}
        />
      </div>
    );
  }

  return <SettingLabel text="Y axis" />;
};
